import { AssetType, SeekResultType, } from '@zattoo/playback-sdk';
import { TimeshiftStatus } from '@zattoo/zapi/lib/stream/enums';
import { registerTimeshift } from '@zattoo/zapi';
import { ZapiEventKey } from '../event/event-key';
import { getLiveState, getVodState, getReplayRecordingState, getRegisteredTimeshiftState, } from './utils';
export const PlayerWithState = (Base) => {
    return class extends Base {
        zapiWatchResponse = null;
        zapiStreamRequestInfo = null;
        timeshiftInfo = null;
        virtualTimeshiftRegisteredAt = null;
        currentTimeRegistered = null;
        get timeshiftAvailability() {
            // TODO
            return null;
        }
        // warning: only available after watch response is received
        zapiMedia = null;
        playOptions = null;
        #programInfoCallback = null;
        setProgramInfoCallback(callback) {
            this.#programInfoCallback = callback;
        }
        async #getProgramInfo(channelId, timestamp) {
            if (!this.#programInfoCallback) {
                return Promise.reject(new Error('getProgramInfo callback is not set'));
            }
            const programInfo = await this.#programInfoCallback(channelId, timestamp);
            return programInfo;
        }
        updateStreamInformation(streamRequestInfo, playOptions) {
            this.zapiMedia = null;
            this.zapiWatchResponse = null;
            this.playOptions = playOptions;
            this.zapiStreamRequestInfo = streamRequestInfo;
        }
        // We are updating play options to ensure we set proper tracks
        // in case we need to reload the stream.
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        setSubtitlesTrack(track) {
            if (this.playOptions && track?.locale) {
                this.playOptions.preferredSubtitlesLanguage = track.locale;
            }
            return super.setSubtitlesTrack(track);
        }
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        setAudioTrack(track) {
            if (this.playOptions && track.locale) {
                this.playOptions.preferredAudioLanguage = track.locale;
                if (track.audioFormat) {
                    this.playOptions.preferredAudioFormat = track.audioFormat;
                }
            }
            return super.setAudioTrack(track);
        }
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        setPlayerState(stateChange) {
            switch (this.assetType) {
                case AssetType.LIVE: {
                    stateChange.push(getLiveState(this.zapiWatchResponse));
                    break;
                }
                case AssetType.REGISTERED_TIMESHIFT: {
                    stateChange.push(getRegisteredTimeshiftState(this.zapiWatchResponse));
                    break;
                }
                case AssetType.VOD_TRAILER:
                case AssetType.VOD: {
                    stateChange.push(getVodState());
                    break;
                }
                case AssetType.REPLAY:
                case AssetType.RECORDING: {
                    stateChange.push(getReplayRecordingState(this.zapiWatchResponse));
                    break;
                }
                default:
                    break;
            }
            return super.setPlayerState(stateChange);
        }
        zapiHookMediaItem(media, 
        // @ts-expect-error unused param
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        watchResponse) {
            // no-op
            return media;
        }
        zapiDispatchMedia(media, watchResponse) {
            if (!this.zapiWatchResponse) {
                throw new Error('No watch response available');
            }
            this.virtualTimeshiftRegisteredAt = null;
            this.zapiMedia = media;
            // allows mixins to extend the media item.
            // happens after streamInformation is set,
            // so that mixins have all information available
            const extendedMedia = this.zapiHookMediaItem(media, watchResponse);
            this.zapiMedia = extendedMedia;
            this.triggerEvent({
                type: ZapiEventKey.MEDIA_CHANGED,
                media: extendedMedia,
                psid: this.psid ?? undefined,
            });
            return extendedMedia;
        }
        registerVirtualTimeshift() {
            this.virtualTimeshiftRegisteredAt = this.currentPosition;
        }
        triggerFullEvent(event, options) {
            // @ts-expect-error event mismatch
            super.triggerFullEvent(event, options);
        }
        triggerEvent(event) {
            // @ts-expect-error event mismatch
            super.triggerEvent(event);
        }
        // @ts-expect-error unused
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        requestStream(streamRequestInfo) {
            // no op -- never called
            return Promise.reject();
        }
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        seek(position) {
            if (!this.currentPosition ||
                !this.playOptions ||
                // the following conditions check if we can handle seek to replay here:
                this.zapiStreamRequestInfo?.assetType !== AssetType.LIVE ||
                this.zapiWatchResponse?.unregistered_timeshift !== TimeshiftStatus.AVAILABLE ||
                !this.canSeekBackward) {
                return super.seek(position);
            }
            const seekBackward = position - this.currentPosition < 0;
            const seekBackwardBeyondTimeshiftEdge = (seekBackward && (!this.virtualTimeshiftRegisteredAt ||
                position < this.virtualTimeshiftRegisteredAt));
            if (!this.virtualTimeshiftRegisteredAt) {
                this.registerVirtualTimeshift();
            }
            if (!seekBackwardBeyondTimeshiftEdge) {
                return super.seek(position);
            }
            const channelId = this.zapiStreamRequestInfo.watchRequestParams.cid;
            this.#getProgramInfo(this.zapiStreamRequestInfo.watchRequestParams.cid, this.currentPosition).then((programInfo) => {
                if (!programInfo.programId) {
                    this.handleError(new Error('Program ID is not available for replay stream'));
                    return;
                }
                this.playProgram(channelId, programInfo.programId, {
                    ...this.playOptions,
                    gt12ChannelInfo: this.playOptions?.gt12ChannelInfo ? {
                        ...this.playOptions?.gt12ChannelInfo,
                        disableStartAd: true,
                    } : undefined,
                    autoplay: !this.isPausedByUser,
                    startupPosition: position - programInfo.start,
                }).catch((error) => {
                    this.handleError(error);
                    // fall back to current live stream if replay fails
                    if (this.playOptions) {
                        this.playLive(channelId, {
                            ...this.playOptions,
                            startupPosition: undefined,
                        }).catch((liveError) => {
                            this.handleError(liveError);
                        });
                    }
                });
            }).catch((error) => {
                this.handleError(error);
                // can't get program info - seek to last possible position if possible
                if (this.virtualTimeshiftRegisteredAt) {
                    super.seek(this.virtualTimeshiftRegisteredAt);
                }
            });
            // seekPosition is used to handle cumulative seek.
            // We don't want cumulative seek when switching to replay.
            this.cleanupCumulativeSeek();
            return {
                type: SeekResultType.STREAM_CHANGE,
            };
        }
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        pause() {
            if (!this.zapiWatchResponse || !this.zapiStreamRequestInfo) {
                super.pause();
                return;
            }
            switch (this.zapiStreamRequestInfo.assetType) {
                case AssetType.LIVE:
                    if (this.zapiWatchResponse.unregistered_timeshift === TimeshiftStatus.AVAILABLE) {
                        if (!this.canPause) {
                            return;
                        }
                        if (!this.virtualTimeshiftRegisteredAt) {
                            this.registerVirtualTimeshift();
                        }
                        super.pause();
                        return;
                    }
                    if (this.zapiWatchResponse.register_timeshift !== TimeshiftStatus.AVAILABLE) {
                        return;
                    }
                    registerTimeshift({ cid: this.zapiStreamRequestInfo.watchRequestParams.cid })
                        .then((response) => {
                        this.currentTimeRegistered = this.currentPosition;
                        this.timeshiftInfo = response;
                    });
                    super.pause();
                    return;
                default:
                    super.pause();
            }
        }
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/48125
        play() {
            if (!this.zapiWatchResponse || !this.zapiStreamRequestInfo || !this.timeshiftInfo || !this.playOptions) {
                super.play();
                return;
            }
            switch (this.zapiStreamRequestInfo.assetType) {
                case AssetType.LIVE:
                    {
                        const playOptions = this.playOptions;
                        const startupPosition = this.currentTimeRegistered ?? undefined;
                        const cid = this.zapiStreamRequestInfo.watchRequestParams.cid;
                        this.requestStream({
                            playOptions: {
                                ...playOptions,
                                startupPosition,
                            },
                            assetType: AssetType.REGISTERED_TIMESHIFT,
                            watchRequestParams: {
                                cid,
                            },
                        }).catch((error) => {
                            this.handleError(error);
                        });
                    }
                    break;
                default:
                    super.play();
            }
        }
    };
};
